import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Container } from '../../containers/container';
import { LoadingService } from '../../services/loading.service';
import * as fromGraphQl from './graphql';
import { ConnectedDevice } from '../../entities/connected-device/connected-device';
import { ToastService } from '../../services/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { RolesService } from '../../services/roles/roles.service';

interface Setting {
  key: string;
  value: any;
}

@Component({
  selector: 'app-mobile-device-view',
  templateUrl: './mobile-device-view.component.html',
  styleUrls: ['./mobile-device-view.component.scss']
})
export class MobileDeviceViewComponent
  extends Container<fromGraphQl.ConnectedDeviceQuery>
  implements OnInit, OnDestroy {

  dialogRef: DynamicDialogRef | undefined;

  public mobileDevice: ConnectedDevice | undefined;
  public id: string;
  public routeSubscription: Subscription;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    loadingService: LoadingService,
    apollo: Apollo,
    @Inject('env') environment : any,
    private toastService: ToastService,
    private translate: TranslateService,
    private rolesService: RolesService
  ) {
    super(apollo, loadingService, fromGraphQl.CONNECTED_DEVICE_QUERY, environment);
  }

  ngOnInit(): void {
    this.routeSubscription = this.route.params
      .pipe(
        filter((params) => params['id'] !== undefined),
        take(1)
      )
      .subscribe((params) => {
        this.id = params['id'];
        this.subscribeToQuery({
          id: this.id,
        });
      });
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  handleSubscriptionResult(data: fromGraphQl.ConnectedDeviceQuery): void {
    this.loadingService.setLoading(false);
    if (data) {
      this.queryRef.stopPolling();
      console.log(data);
      if (data.getConnectedDevice) {
        this.mobileDevice = new ConnectedDevice(data.getConnectedDevice);
      }
    }
  }

  copyIdToClipboard(stringToCopy: string): void {
    navigator.clipboard.writeText(stringToCopy).then(() => {
      this.toastService.addToastWithMessage(this.translate.instant('coreModule.mobileDeviceViewComponent.id.copyToClipboard.successMessage'));
    }).catch(err => {
      // error
    });
  }

    calculateSeverity(updateState: string): "success" | "secondary" | "info" | "warning" | "danger" | "contrast" {
   // NO_UPDATE, SHOULD_UPDATE, MUST_UPDATE
   switch (updateState) {
      case 'NO_UPDATE':
        return 'success';
      case 'SHOULD_UPDATE':
        return 'warning';
      case 'MUST_UPDATE':
        return 'danger';
      default:
        return 'success';
    }
  }

  updateLabel(updateState: string): string {
    // NO_UPDATE, SHOULD_UPDATE, MUST_UPDATE 
    switch (updateState) { 
      case 'NO_UPDATE':
        return 'coreModule.mobileDeviceViewComponent.generalDetails.updateState.noUpdate';
      case 'SHOULD_UPDATE':
        return 'coreModule.mobileDeviceViewComponent.generalDetails.updateState.shouldUpdate';
      case 'MUST_UPDATE':
        return 'coreModule.mobileDeviceViewComponent.generalDetails.updateState.mustUpdate';
      default:
        return 'coreModule.mobileDeviceViewComponent.generalDetails.updateState.noUpdate';
    }
  }

  isAdmin(): boolean {
    return this.rolesService.hasRole('framework_tenants_manage');
  }
  gotToUrl(url: string): void {
    if (url) {
      window.open(url, '_blank');
    }
  }
 
  // the list is like this: { key: 'key1', value: 'value1' }
  // the function returns a list of keys, where the value is true
  getPermissions(permissions: any): string {
    if (permissions) {
      // If permissions is an array of objects: [{ key: 'key1', value: true }, ...]
      if (Array.isArray(permissions)) {
        const keys = permissions   
          .filter((item: any) => item.value === true)
          .map((item: any) => this.translate.instant(`coreModule.mobileDeviceViewComponent.${item.key}`));
        return keys.length > 0 ? keys.join(', ') : '-';
      }
      // If permissions is an object: { key1: true, key2: false, ... }
      const keys = Object.keys(permissions)
        .filter(key => permissions[key] === true)
        .map(key => this.translate.instant(`coreModule.mobileDeviceViewComponent.${key}`));
      return keys.length > 0 ? keys.join(', ') : '-';
    }
    return '-';
  }
}
